import * as React from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import ExportIcon from "components/icons/ExportIcon";
import ApplicationLayout from "components/layout/ApplicationLayout";
import AssignKeysView from "components/licenses/assign-bms-keys/AssignKeysView";
import BulkImportKeysView from "components/licenses/bulk-import-license-key/BulkImportKeysView";
import { BLANCCO_TOKEN_ID, OEM_LICENSE_ID } from "components/licenses/common";
import SerialNumberKeysTable from "components/licenses/SerialNumberKeysTable";
import { LICENSES_ROUTE } from "components/router/Routes";
import { AUTH_LICENSE_KEY_MANAGEMENT } from "domain/authority";
import { LicenseKeyFilter } from "domain/licenseKeys";
import { LicenseData } from "domain/licenses";
import { licenseService } from "services/licenses/LicenseService";
import { getStanUrl } from "services/login/endpointRepository";
import { isLicensingModelBundleWithToken } from "services/tenants/tenantCookieService";
import { userSessionService } from "services/user/UserSessionService";
import buttonStyle from "styles/buttons.scss";
import { ParameterQuery } from "utils/commonFunctions";
import { isExpired } from "utils/format";
import { logger } from "utils/logging";
import { useTheme } from "utils/useTheme";

import testIds from "testIds.json";

function SerialNumberKeysView(): JSX.Element {
    const { t } = useTranslation();
    const [state, setState] = useState(0);
    const [availableLicenses, setAvailableLicenses] = React.useState<LicenseData[]>([]);
    const [allowOemLicenseKey, setAllowOemLicenseKeys] = React.useState<boolean>(false);
    const [totalAvailableLicenses, setTotalAvailableLicenses] = useState<number>(0);
    const [oemLicenseExpirationDate, setOemLicenseExpirationDate] = useState<string>("");
    const PRODUCT_ON_TIER_SELECTED = isLicensingModelBundleWithToken() ? BLANCCO_TOKEN_ID : OEM_LICENSE_ID;

    const [showExportButton, setShowExportButton] = React.useState<boolean>(false);
    const [filterType, setFilterType] = React.useState<LicenseKeyFilter>(LicenseKeyFilter.ALL);
    const [quickSearch, setQuickSearch] = React.useState<string>();
    const theme = useTheme();
    function incrementState(): void {
        setState((previous) => previous + 1);
    }

    function fetchOemLicenses() {
        const abortController = new AbortController();

        licenseService
            .fetchAvailableLicenses(PRODUCT_ON_TIER_SELECTED, abortController.signal)
            .then((data) => {
                if (data != undefined) {
                    setAllowOemLicenseKeys(true);
                    setAvailableLicenses([
                        {
                            type: OEM_LICENSE_ID,
                            available: data.availableLicenses,
                            expirationDate: data.expiration,
                        } as LicenseData,
                    ]);
                }
            })
            .catch((err) => {
                logger.error("Exception occurred while fetching available licenses: ", err);
            });
    }
    React.useEffect(() => {
        fetchOemLicenses();
    }, []);

    React.useEffect(() => {
        const license = availableLicenses.find((license) => license.type === OEM_LICENSE_ID);
        if (license) {
            setTotalAvailableLicenses(license.available);
            setOemLicenseExpirationDate(license.expirationDate);
        }
    }, [availableLicenses]);

    const exportSerialNumberKeys = () => {
        window.location.href =
            getStanUrl() +
            "/license-keys/export" +
            new ParameterQuery()
                .add("oem", "true")
                .add("search", quickSearch)
                .add("filter", filterType)
                .createQueryString();
    };

    const createViewActions = React.useCallback(() => {
        const exportButton = showExportButton ? (
            <button
                onClick={exportSerialNumberKeys}
                className={buttonStyle.secondaryButtonWithoutIcon}
                data-testid={testIds.workArea.license.serialNumber.exportKeysButton}
            >
                <ExportIcon color={theme.iconFillColor} size={16} />
                {t("LicenseKeys.export.title")}
            </button>
        ) : null;
        if (
            availableLicenses.length < 1 ||
            isExpired(availableLicenses[0].expirationDate) ||
            availableLicenses[0].available < 1
        ) {
            return <>{exportButton}</>;
        }
        const assignButton =
            allowOemLicenseKey && userSessionService.userHasAllAuthorities([AUTH_LICENSE_KEY_MANAGEMENT]) ? (
                <AssignKeysView updateTable={incrementState} availableLicenses={availableLicenses} fromBms={false} />
            ) : undefined;
        const importKeysButton =
            allowOemLicenseKey && userSessionService.userHasAllAuthorities([AUTH_LICENSE_KEY_MANAGEMENT]) ? (
                <BulkImportKeysView
                    totalAvailableLicenses={totalAvailableLicenses}
                    availableLicenses={availableLicenses}
                    oemLicenseExpirationDate={oemLicenseExpirationDate}
                />
            ) : undefined;
        return (
            <>
                {importKeysButton}
                {exportButton}
                {assignButton}
            </>
        );
    }, [availableLicenses, allowOemLicenseKey, totalAvailableLicenses, showExportButton, filterType]);

    return (
        <ApplicationLayout
            viewTitle={t("LicenseKeys.serialNumberKeys")}
            view={
                <SerialNumberKeysTable
                    count={state}
                    setExportButtonVisibility={setShowExportButton}
                    filterType={filterType}
                    setFilterType={setFilterType}
                    setQuickSearch={setQuickSearch}
                    quickSearch={quickSearch}
                />
            }
            mainRoute={LICENSES_ROUTE}
            viewActions={createViewActions()}
        />
    );
}

export default SerialNumberKeysView;
